/*
 * Purpose: Shared Mission map visuals — glass tiles + curved connectors (preview + Builder)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import {
  Bot,
  CheckCircle,
  Plane,
  Play,
  Route,
  Target,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { Node } from '@/stores/workflowStore';
import { isMegaBrandedBlock } from '@/lib/brandedBlocks';

export const MAP_NODE_WIDTH = 192;
export const MAP_NODE_WIDTH_MEGA = 256;
export const MAP_NODE_HEIGHT = 118;
export const MAP_NODE_HEIGHT_MEGA = 152;

export const PREVIEW_NODE_WIDTH = 148;
export const PREVIEW_NODE_HEIGHT = 52;

export interface MapNodeVisualStyle {
  bg: string;
  border: string;
  icon: LucideIcon;
  iconClass: string;
}

export const MAP_NODE_VISUAL_STYLES: Record<string, MapNodeVisualStyle> = {
  start: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/45',
    icon: Play,
    iconClass: 'text-emerald-600 dark:text-emerald-400',
  },
  goal: {
    bg: 'bg-ab-green-mist/60 dark:bg-ab-green-forest/30',
    border: 'border-ab-green-core/40 dark:border-ab-green-glow/40',
    icon: Target,
    iconClass: 'text-ab-green-deep dark:text-ab-green-glow',
  },
  agent: {
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/40',
    icon: Bot,
    iconClass: 'text-violet-600 dark:text-violet-400',
  },
  drone: {
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/40',
    icon: Plane,
    iconClass: 'text-sky-600 dark:text-sky-400',
  },
  tube: {
    bg: 'bg-ab-green-mist/40 dark:bg-ab-green-forest/20',
    border: 'border-dashed border-ab-green-core/45 dark:border-ab-green-glow/45',
    icon: Route,
    iconClass: 'text-ab-green-core dark:text-ab-green-glow',
  },
  trigger: {
    bg: 'bg-muted/40',
    border: 'border-border/70',
    icon: Zap,
    iconClass: 'text-muted-foreground',
  },
  tool: {
    bg: 'bg-muted/40',
    border: 'border-border/70',
    icon: Wrench,
    iconClass: 'text-muted-foreground',
  },
  action: {
    bg: 'bg-muted/40',
    border: 'border-border/70',
    icon: CheckCircle,
    iconClass: 'text-ab-green-sea dark:text-ab-green-glow',
  },
};

export function getMapNodeVisualStyle(node: Node): MapNodeVisualStyle {
  return MAP_NODE_VISUAL_STYLES[node.type] ?? MAP_NODE_VISUAL_STYLES.tool;
}

export function getMapNodeDimensions(node: Node): { width: number; height: number } {
  const isMega = node.type === 'branded' && isMegaBrandedBlock(node.subtype);
  return {
    width: isMega ? MAP_NODE_WIDTH_MEGA : MAP_NODE_WIDTH,
    height: isMega ? MAP_NODE_HEIGHT_MEGA : MAP_NODE_HEIGHT,
  };
}

import type { ResolvedMapFlowOrientation } from '@/lib/mapFlowOrientation';

export interface MapEdgePathOptions {
  toWidth?: number;
  toHeight?: number;
  orientation?: ResolvedMapFlowOrientation;
}

/** Cubic-bezier connector — horizontal (LR) or vertical (TB) */
export function buildMapEdgePath(
  from: Pick<Node, 'x' | 'y'>,
  to: Pick<Node, 'x' | 'y'>,
  fromWidth: number,
  fromHeight: number,
  toHeightOrOpts: number | MapEdgePathOptions = fromHeight,
  maybeOpts?: MapEdgePathOptions
): string {
  const opts: MapEdgePathOptions =
    typeof toHeightOrOpts === 'number'
      ? { toHeight: toHeightOrOpts, ...maybeOpts }
      : toHeightOrOpts;
  const toHeight = opts.toHeight ?? fromHeight;
  const toWidth = opts.toWidth ?? fromWidth;
  const orientation = opts.orientation ?? 'horizontal';

  if (orientation === 'vertical') {
    const x1 = from.x + fromWidth / 2;
    const y1 = from.y + fromHeight;
    const x2 = to.x + toWidth / 2;
    const y2 = to.y;
    const midY = (y1 + y2) / 2;
    return `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
  }

  const x1 = from.x + fromWidth;
  const y1 = from.y + fromHeight / 2;
  const x2 = to.x;
  const y2 = to.y + toHeight / 2;
  const midX = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
}

/** Journey tube — gentle arc offset from the straight midline */
export function buildMapJourneyTubePath(
  from: Pick<Node, 'x' | 'y'>,
  to: Pick<Node, 'x' | 'y'>,
  fromWidth: number,
  fromHeight: number,
  toHeightOrOpts: number | MapEdgePathOptions = fromHeight,
  maybeOpts?: MapEdgePathOptions
): { path: string; labelX: number; labelY: number } {
  const opts: MapEdgePathOptions =
    typeof toHeightOrOpts === 'number'
      ? { toHeight: toHeightOrOpts, ...maybeOpts }
      : toHeightOrOpts;
  const toHeight = opts.toHeight ?? fromHeight;
  const toWidth = opts.toWidth ?? fromWidth;
  const orientation = opts.orientation ?? 'horizontal';

  if (orientation === 'vertical') {
    const x1 = from.x + fromWidth / 2;
    const y1 = from.y + fromHeight;
    const x2 = to.x + toWidth / 2;
    const y2 = to.y;
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    return {
      path: `M ${x1} ${y1} Q ${midX + 28} ${midY} ${x2} ${y2}`,
      labelX: midX + 34,
      labelY: midY,
    };
  }

  const x1 = from.x + fromWidth;
  const y1 = from.y + fromHeight / 2;
  const x2 = to.x;
  const y2 = to.y + toHeight / 2;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  return {
    path: `M ${x1} ${y1} Q ${midX} ${midY - 28} ${x2} ${y2}`,
    labelX: midX,
    labelY: midY - 34,
  };
}
